import { useState } from 'react';
import { Webhook } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useReplayWebhookMutation,
  useWebhookEventQuery,
  useWebhookEventsQuery,
} from '@/client/api/payment/finance';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import Pagination from '@/client/components/common/Pagination';
import { EmptyRow, ErrorNote, FinancePage, card, fmtDate, selectCls, td, th } from './shell';

const LIMIT = 50;
const PROCESSORS = ['paystack', 'expresspay', 'momo', 'hubtel'];

const WebhookLogPage = () => {
  const [processor, setProcessor] = useState('');
  const [accepted, setAccepted] = useState('');
  const [reference, setReference] = useState('');
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<string | null>(null);

  const { data, isLoading, error } = useWebhookEventsQuery({
    ...(processor ? { processor } : {}),
    ...(accepted ? { accepted: Number(accepted) as 0 | 1 } : {}),
    ...(reference.trim() ? { reference: reference.trim() } : {}),
    limit: LIMIT,
    offset: (page - 1) * LIMIT,
  });
  const detail = useWebhookEventQuery(openId);
  const replay = useReplayWebhookMutation();
  const doReplay = (id: string) =>
    replay.mutate(
      { id },
      {
        onSuccess: () => toast.success('Event queued for processing', { richColors: true }),
        onError: (e: any) => toast.error(e?.message || 'Could not replay the event', { richColors: true }),
      },
    );
  const reset = <T,>(set: (v: T) => void) => (v: T) => {
    setPage(1);
    set(v);
  };

  return (
    <FinancePage
      icon={Webhook}
      title="Webhook log"
      description="Every payment webhook we received, with the full payload and the answer we gave. Events are kept for 90 days. A Paystack event can be replayed; replaying one that was already processed changes nothing."
      actions={
        <div className="flex flex-wrap gap-2">
          <Input
            value={reference}
            onChange={(e) => reset(setReference)(e.target.value)}
            placeholder="Exact reference"
            aria-label="Reference"
            className="w-56"
          />
          <select
            aria-label="Processor"
            className={selectCls}
            value={processor}
            onChange={(e) => reset(setProcessor)(e.target.value)}
          >
            <option value="">All processors</option>
            {PROCESSORS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          <select
            aria-label="Outcome"
            className={selectCls}
            value={accepted}
            onChange={(e) => reset(setAccepted)(e.target.value)}
          >
            <option value="">Any outcome</option>
            <option value="1">Accepted</option>
            <option value="0">Rejected</option>
          </select>
        </div>
      }
    >
      <ErrorNote error={error} />
      <div className={card}>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead>
              <tr className="bg-gray-50">
                {['Received', 'Processor', 'Event', 'Reference', 'Account', 'Answer', ''].map((h, i) => (
                  <th key={i} className={th}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {!isLoading && !data?.events?.length && <EmptyRow cols={7}>No webhook events</EmptyRow>}
              {data?.events?.map((e) => (
                <tr key={e.id}>
                  <td className={`${td} text-gray-500`}>{fmtDate(e.created_at)}</td>
                  <td className={td}>{e.processor}</td>
                  <td className={`${td} font-mono`}>{e.event || '—'}</td>
                  <td className={`${td} font-mono`}>{e.reference || '—'}</td>
                  <td className={td}>{e.mode || '—'}</td>
                  <td className={td}>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        e.accepted ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
                      }`}
                    >
                      {e.accepted ? 'Accepted' : 'Rejected'} · {e.status_code}
                    </span>
                  </td>
                  <td className={td}>
                    <div className="flex justify-end gap-2">
                      <Button type="button" size="sm" variant="outline" onClick={() => setOpenId(e.id)}>
                        View
                      </Button>
                      {e.processor === 'paystack' && (
                        <PermissionButton
                          code="payment.manage"
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={replay.isPending}
                          onClick={() => doReplay(e.id)}
                        >
                          Replay
                        </PermissionButton>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="border-t border-gray-100 px-5 py-3">
          <Pagination
            total={data?.pagination?.total ?? 0}
            page={page}
            itemsPerPage={LIMIT}
            limit={LIMIT}
            setPage={setPage}
            showButtons
            showSelect={false}
          />
        </div>
      </div>

      <Dialog open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {detail.data?.processor} {detail.data?.event || 'event'}
            </DialogTitle>
            <DialogDescription>
              Received {fmtDate(detail.data?.created_at)} · answered {detail.data?.status_code}
              {detail.data?.trace_id ? ` · trace ${detail.data.trace_id}` : ''}
            </DialogDescription>
          </DialogHeader>
          <pre className="max-h-[50vh] overflow-auto rounded-lg bg-gray-50 p-4 text-xs text-gray-800">
            {detail.isLoading
              ? 'Loading…'
              : detail.data?.payload == null
                ? 'The payload was too large to store.'
                : JSON.stringify(detail.data.payload, null, 2)}
          </pre>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpenId(null)}>
              Close
            </Button>
            {detail.data?.processor === 'paystack' && (
              <PermissionButton
                code="payment.manage"
                type="button"
                disabled={replay.isPending}
                onClick={() => openId && doReplay(openId)}
              >
                Replay
              </PermissionButton>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </FinancePage>
  );
};

export default WebhookLogPage;
